import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '../../layout/memberPrimitives';
import type { NextStepAction, NextStepAccent } from '../../../utils/dashboard/nextStep';

const ACCENT: Record<NextStepAccent, { bar: string; button: string; eyebrow: string }> = {
  oxblood: { bar: '#5C1A1C', button: '#5C1A1C', eyebrow: '#5C1A1C' },
  terracotta: { bar: 'var(--melio-terracotta-deep)', button: 'var(--melio-terracotta-deep)', eyebrow: 'var(--melio-terracotta-deep)' },
  olive: { bar: 'var(--melio-olive-deep)', button: 'var(--melio-olive-deep)', eyebrow: 'var(--melio-olive-deep)' },
  sea: { bar: 'var(--melio-sea-deep)', button: 'var(--melio-sea-deep)', eyebrow: 'var(--melio-sea-deep)' },
};

/**
 * The one thing the member should do next.
 *
 * `action === null` is a designed state, not an empty one: a dashboard whose
 * hero can go blank reads as broken, so a caught-up member gets an affirmative
 * panel instead.
 *
 * But "caught up" and "brand new" are not the same member, and they used to get
 * the identical "You're all set — nothing needs your attention" panel, because
 * nextStep returns null in both cases. Telling someone on day one that they are
 * all set is a reassurance they have not earned yet and gives them nothing to
 * do; `hasRecord` splits the two.
 */
export default function NextStepHero({
  action,
  firstName,
  hasRecord = true,
}: {
  action: NextStepAction | null;
  firstName: string;
  hasRecord?: boolean;
}) {
  // Olive reads as "nothing is being asked of you" and is right for the
  // caught-up panel. A brand-new member is being asked for something, so that
  // variant takes the same oxblood as a real next step — otherwise its rule and
  // its button disagree about how much the panel matters.
  const accent = action ? ACCENT[action.accent] : hasRecord ? ACCENT.olive : ACCENT.oxblood;

  return (
    <section
      className="grid grid-cols-1 md:grid-cols-[1fr_340px] gap-6 md:gap-10 pb-7"
      style={{ borderBottom: '1px solid var(--melio-border)' }}
      aria-labelledby="dashboard-greeting"
    >
      <div>
        {/* The greeting was set one step larger than this, which made the
            member's own name the biggest thing on a page whose job is to answer
            "what do I need to do next" — the answer itself sat beside it at
            two-thirds the size. A greeting should be warm, not loud. */}
        <p className="m-eyebrow mb-2">{hasRecord ? 'Welcome back' : 'Welcome to Melio'}</p>
        <h1 id="dashboard-greeting" className="m-display-2">
          {firstName}.
        </h1>
        <p
          className="mt-5 pt-4 text-lg md:text-xl italic leading-relaxed max-w-xl"
          style={{
            fontFamily: "var(--melio-quote)",
            color: 'var(--melio-ink)',
            borderTop: '1px solid var(--melio-border)',
          }}
        >
          We hold a record, read it carefully, and write to you each quarter. That is the whole idea.
        </p>
      </div>

      <aside className="pl-[18px]" style={{ borderLeft: `3px solid ${accent.bar}` }}>
        {action ? (
          <>
            <p className="m-eyebrow text-[10px] mb-1" style={{ color: accent.eyebrow }}>
              {action.eyebrow}
            </p>
            <h2 className="m-title mb-2">{action.title}</h2>
            <p className="text-sm mb-4" style={{ color: 'var(--melio-text-muted)' }}>
              {action.body}
            </p>
            {/* This is the only primary CTA on the dashboard and it used to be
                the least prominent interactive element on it — a 12px uppercase
                block among 24px-rounded cards. It is the answer to "what do I
                do next", so it is now sized and weighted like one. */}
            <Link
              to={action.ctaHref}
              className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-full transition-transform hover:-translate-y-px"
              style={{
                backgroundColor: accent.button,
                color: 'var(--melio-cream-warm)',
                boxShadow: 'var(--e-1)',
              }}
            >
              {action.ctaLabel}
              <ChevronRightIcon size={16} />
            </Link>
          </>
        ) : hasRecord ? (
          <>
            <p className="m-eyebrow text-[10px] mb-1">This quarter</p>
            <h2 className="m-title-sm mb-1.5">You&apos;re all set</h2>
            <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
              Nothing needs your attention right now. We&apos;ll write when your next report is ready.
            </p>
          </>
        ) : (
          <>
            <p className="m-eyebrow text-[10px] mb-1" style={{ color: accent.eyebrow }}>
              Your first step
            </p>
            {/* Matches the action variant's weight — this one also carries a
                CTA. "You're all set" stays a step quieter on purpose: it is
                reassurance, and nothing is being asked of the member. */}
            <h2 className="m-title mb-2">Let&apos;s build your baseline</h2>
            {/* Points at /history, not at booking. The history is the first
                step and the interview follows it; this panel used to say
                "there is nothing to fill in beforehand" and send members
                straight to the calendar, which is how they ended up with an
                appointment and an empty record. */}
            <p className="text-sm mb-4" style={{ color: 'var(--melio-text-muted)' }}>
              Your record is empty for now. Start with your health and family history — it is what
              your care team reads first, and what your clinical history interview works from.
            </p>
            <Link
              to="/history"
              className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-full transition-transform hover:-translate-y-px"
              style={{
                backgroundColor: accent.button,
                color: 'var(--melio-cream-warm)',
                boxShadow: 'var(--e-1)',
              }}
            >
              Start your health history
              <ChevronRightIcon size={16} />
            </Link>
          </>
        )}
      </aside>
    </section>
  );
}
